import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { INTEREST_CATEGORIES } from '../constants/doctorProfile';

// Shared multi-select for the cardiac-interests taxonomy, used everywhere a
// person's own interests get edited (attendee signup/profile, speaker
// onboarding/dashboard) or an admin edits someone else's (SpeakerDetail,
// UserManagement). Interests are stored as a flat list of leaf values -
// categories are a pure display grouping (see constants/doctorProfile.js) -
// so "select the whole category" is just "add/remove every leaf value in it"
// and needs no data-model change.
//
// Collapsed by default so ~50 leaf options don't overwhelm the form; each
// category's own checkbox is a tri-state select-all/none for people who just
// want to say "cardiac surgery" without picking every sub-topic, and the
// chevron expands it for anyone who wants to be more specific.

const List = styled.div`display:flex;flex-direction:column;gap:.15rem;`;
const CategoryRow = styled.div`border-bottom:1px solid rgba(15,23,42,.06);&:last-child{border-bottom:0;}`;
const CategoryHeader = styled.div`display:flex;align-items:center;gap:.5rem;padding:${p => (p.$compact ? '.4rem 0' : '.55rem 0')};`;
const CategoryCheckbox = styled.input`flex:none;margin:0;`;
const CategoryName = styled.button`
  flex:1;display:flex;align-items:center;gap:.4rem;background:none;border:0;padding:0;margin:0;
  text-align:left;cursor:pointer;font-weight:700;color:#111D4E;font-size:${p => (p.$compact ? '.82rem' : '.9rem')};
`;
const Count = styled.span`font-weight:600;color:#5B6B78;font-size:.78em;`;
const ExpandBtn = styled.button`flex:none;border:0;background:none;padding:.2rem;cursor:pointer;color:#94a3b8;display:flex;&:hover{color:#5B6B78;}`;
const SubList = styled.div`
  display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.35rem;
  padding:0 0 .6rem 1.6rem;@media(max-width:480px){grid-template-columns:1fr;}
`;
const SubLabel = styled.label`display:flex;gap:.4rem;align-items:flex-start;font-weight:400;font-size:${p => (p.$compact ? '.8rem' : '.85rem')};`;

// Native <input type="checkbox"> has no `indeterminate` prop - it's DOM-only,
// so this has to go through a ref rather than a plain attribute.
const TriStateCheckbox = ({ checked, indeterminate, onChange, ...rest }) => {
  const ref = useRef(null);
  return (
    <CategoryCheckbox
      type="checkbox"
      ref={el => { ref.current = el; if (el) el.indeterminate = indeterminate; }}
      checked={checked}
      onChange={onChange}
      {...rest}
    />
  );
};

const InterestPicker = ({ value, onChange, compact = false }) => {
  const intl = useIntl();
  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const interestLabel = interest => t(`speaker.interest.${interest}`, interest);
  const selected = value || [];
  const [expanded, setExpanded] = useState({});

  const selectedCount = category => category.interests.filter(i => selected.includes(i)).length;
  const toggleExpand = key => setExpanded(current => ({ ...current, [key]: !current[key] }));

  const toggleCategory = category => {
    const count = selectedCount(category);
    if (count === category.interests.length) {
      onChange(selected.filter(i => !category.interests.includes(i)));
    } else {
      const additions = category.interests.filter(i => !selected.includes(i));
      onChange([...selected, ...additions]);
    }
  };

  const toggleInterest = interest => {
    onChange(selected.includes(interest) ? selected.filter(i => i !== interest) : [...selected, interest]);
  };

  return (
    <List>
      {INTEREST_CATEGORIES.map(category => {
        const count = selectedCount(category);
        const isExpanded = !!expanded[category.key];
        return (
          <CategoryRow key={category.key}>
            <CategoryHeader $compact={compact}>
              <TriStateCheckbox
                checked={count === category.interests.length}
                indeterminate={count > 0 && count < category.interests.length}
                onChange={() => toggleCategory(category)}
              />
              <CategoryName type="button" $compact={compact} onClick={() => toggleExpand(category.key)}>
                {t(category.labelId, category.labelDefault)}
                {count > 0 && <Count>({count}/{category.interests.length})</Count>}
              </CategoryName>
              <ExpandBtn type="button" onClick={() => toggleExpand(category.key)} aria-label={isExpanded ? t('interestPicker.collapse', 'Collapse') : t('interestPicker.expand', 'Expand')}>
                {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </ExpandBtn>
            </CategoryHeader>
            {isExpanded && (
              <SubList>
                {category.interests.map(interest => (
                  <SubLabel key={interest} $compact={compact}>
                    <input type="checkbox" checked={selected.includes(interest)} onChange={() => toggleInterest(interest)} />
                    {interestLabel(interest)}
                  </SubLabel>
                ))}
              </SubList>
            )}
          </CategoryRow>
        );
      })}
    </List>
  );
};

export default InterestPicker;
